import {useState, useEffect, useCallback, useRef} from 'react'

interface UseApiRequestOptions {
  skip?: boolean
  cacheTimeMs?: number
  keepPreviousData?: boolean
}

type RequestStatus = 'idle' | 'loading' | 'success' | 'error'

type CacheEntry<T> = {
  promise?: Promise<T>
  data?: T
  error?: any
  timestamp: number
}

interface UseApiRequestResult<T> {
  data: T | null
  error: Error | null
  isLoading: boolean
  isDataForCurrentParams?: boolean
  refetch: () => void
}

// Shared cache across component instances
const requestCache = new Map<string, CacheEntry<any>>()

// Map to assign stable IDs to API functions for cache key differentiation
const apiFnIdMap = new WeakMap<(...args: any[]) => any, string>()
let apiFnIdCounter = 0

function getApiFnId(fn: (...args: any[]) => any): string {
  let id = apiFnIdMap.get(fn)
  if (!id) {
    id = `fn_${apiFnIdCounter++}`
    apiFnIdMap.set(fn, id)
  }
  return id
}

function useApiRequest<T, P extends Record<string, any>>(
  apiFn: (params: P) => Promise<T>,
  params: P | null,
  options?: UseApiRequestOptions,
): UseApiRequestResult<T> {
  const {skip = false, cacheTimeMs = 500, keepPreviousData = false} = options || {}

  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [status, setStatus] = useState<RequestStatus>('idle')
  const [refetchToken, setRefetchToken] = useState(0)
  const loadedParamsKey = useRef<string | null>(null)
  const dataRef = useRef<T | null>(null)
  dataRef.current = data

  // Keep a stable ref to the latest apiFn so it never triggers the effect (from QA)
  const apiFnRef = useRef(apiFn)
  apiFnRef.current = apiFn

  // Track latest request to avoid stale updates (from Dev)
  const activeRequestId = useRef(0)

  // Include the API function identity in the cache key to prevent collisions
  // between different API functions called with the same params
  const fnId = getApiFnId(apiFn)
  const paramsKey = params ? `${fnId}:${JSON.stringify(params)}` : null

  useEffect(() => {
    if (!params || !paramsKey || skip) {
      setStatus('idle')
      return
    }

    const now = Date.now()
    const requestId = ++activeRequestId.current
    const isNewParams = loadedParamsKey.current !== null && loadedParamsKey.current !== paramsKey
    if (isNewParams) {
      setData(null)
      dataRef.current = null
      setError(null)
      setStatus('loading')
    }
    const cached = requestCache.get(paramsKey)

    // 1. Use cached data if fresh
    if (cached?.data && now - cached.timestamp < cacheTimeMs) {
      setData(cached.data)
      dataRef.current = cached.data
      loadedParamsKey.current = paramsKey
      setError(null)
      setStatus('success')
      return
    }

    // 2. Reuse in-flight request
    if (cached?.promise) {
      if (!(keepPreviousData && dataRef.current !== null) || isNewParams) setStatus('loading')

      cached.promise
        .then(res => {
          if (activeRequestId.current !== requestId) return
          setData(res)
          dataRef.current = res
          loadedParamsKey.current = paramsKey
          setError(null)
          setStatus('success')
        })
        .catch(err => {
          if (activeRequestId.current !== requestId) return
          setError(err instanceof Error ? err : new Error('API Error'))
          setData(null)
          setStatus('error')
        })

      return
    }

    // 3. Create new request
    if (!(keepPreviousData && dataRef.current !== null) || isNewParams) setStatus('loading')

    const promise = apiFnRef.current(params)

    // Store promise immediately (critical for dedupe)
    requestCache.set(paramsKey, {promise, timestamp: now})

    // Effect cleanup — cancels stale updates (from QA)
    let isCurrentRequest = true

    promise
      .then(res => {
        requestCache.set(paramsKey, {data: res, timestamp: Date.now()})

        if (!isCurrentRequest || activeRequestId.current !== requestId) return
        setData(res)
        dataRef.current = res
        loadedParamsKey.current = paramsKey
        setError(null)
        setStatus('success')
      })
      .catch(err => {
        requestCache.set(paramsKey, {error: err, timestamp: Date.now()})

        if (!isCurrentRequest || activeRequestId.current !== requestId) return
        setError(err instanceof Error ? err : new Error('API Error'))
        setData(null)
        setStatus('error')
      })

    return () => {
      isCurrentRequest = false
    }
  }, [paramsKey, skip, refetchToken, cacheTimeMs, keepPreviousData])

  const refetch = useCallback(() => {
    if (!paramsKey) return
    // Clear cache so new request is forced (from Dev)
    requestCache.delete(paramsKey)
    setRefetchToken(prev => prev + 1)
  }, [paramsKey])

  return {
    data,
    error,
    isLoading: status === 'loading',
    isDataForCurrentParams: loadedParamsKey.current === paramsKey && data !== null,
    refetch,
  }
}

export default useApiRequest
